/**
 * Static-map image proxy URL builder (plan 2026-07-20-002, U4/U5/U6).
 *
 * The backend proxy resolves an event's coordinates server-side from its
 * public id and streams a Mapbox static image — the token never reaches the
 * client, and no caller-supplied lat/lng is ever accepted (KTD2). This helper
 * just builds the `<img src>` both the admin preview (U5) and the public
 * event page (U6) point at.
 */

export type EventMapSurface = 'web_card' | 'mobile_card' | 'admin_preview'

/**
 * Cache-buster derived from coordinates. The proxy responds with a 1-year
 * `immutable` Cache-Control, but its URL is keyed on event id + surface, NOT
 * coordinates — so after an event's location is edited and re-saved, the
 * SAME URL would keep serving a browser/CDN-cached image of the OLD pin.
 * Appending this token changes the URL (and therefore the cache key)
 * whenever the pin actually moves; the backend ignores the extra `v` param
 * (Zod strips unknown query keys) and still resolves coordinates by id.
 */
export function buildCoordVersion(latitude: number, longitude: number): string {
  return `${latitude.toFixed(5)}_${longitude.toFixed(5)}`
}

export function buildEventMapImageUrl(eventPublicId: string, surface: EventMapSurface, version?: string): string {
  const base = process.env.NEXT_PUBLIC_BACKEND_URL ?? ''
  const url = `${base}/v1/event-maps/ticketed-events/${encodeURIComponent(eventPublicId)}?surface=${surface}`
  return version ? `${url}&v=${encodeURIComponent(version)}` : url
}
